/**
 * How a records table turns a row's resolved chain into the text a cell shows. Formatting, not
 * derivation - it sits apart from `useRecordsAggregation`, which is about what a page computes from
 * its loaded rows rather than how those rows read.
 */

import { BLANK_FACET_VALUE } from '@/composables/records/useRecordsAggregation';

/**
 * "Farm / Barn" — barn names repeat across farms, so the barn alone does not identify a location.
 * A registration made at farm level has no barn, so it reads as the farm name alone. `compact` drops
 * the farm when space is tight (see `RecordsTable.vue`'s narrow-viewport check) and does not change
 * the farm-only or `''` (no farm) results.
 */
export function locationLabel(farm: { name: string } | null, barn: { name: string } | null, options?: { compact?: boolean }): string {
    if (!farm) {
        return '';
    }

    if (!barn) {
        return farm.name;
    }

    if (options?.compact) {
        return barn.name;
    }

    return `${farm.name} / ${barn.name}`;
}

/**
 * The facet value a Location column filters on: the barn id, or - for a farm-level registration with
 * no barn - a farm-prefixed key. Farm and barn ids come from the same id space, so they cannot clash;
 * the prefix just makes the key say whether it is a farm or a barn. No farm either falls back to the
 * blank option.
 */
export function locationFacetValue(farm: { id: number } | null, barn: { id: number } | null): string | number {
    if (barn) {
        return barn.id;
    }

    return farm ? `farm-${farm.id}` : BLANK_FACET_VALUE;
}
